import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import Card from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Card';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import useMetaLayoutContext from 'src/Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import AddStrategyButtonPicker from 'Views/Page/Content/Templates/WhatIfTemplate/Components/AddStrategyButtonPicker';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import ModifyStrategyButtonPicker
  from 'Views/Page/Content/Templates/WhatIfTemplate/Components/ModifyStrategyButtonPicker';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import IconButton from '@material-ui/core/IconButton';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import usePopoverState from 'Hooks/usePopoverState';
import Popover from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Popover';
import List from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/List';
import ListItem from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/ListItem';
import NewDialog from 'Components/UI/ConfirmationDialog/NewDialog';
import useConfirmationDialogState from 'Hooks/useConfirmationDialogState';
import fr from 'facet-rest';
import { Accordion, AccordionDetails, AccordionSummary } from '@material-ui/core';
import QuickEditPicker
  from 'Views/Page/Content/Templates/WhatIfTemplate/Content/RightSide/BottomPart/SummaryCardPicker/QuickEditPicker';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import AccountTable
  from 'Views/Page/Content/Templates/WhatIfTemplate/Content/RightSide/BottomPart/SummaryCardPicker/AccordionSummaryCard/Content/AccountTable';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import { FDStack } from '@facet-wealth/design';
import OutcomeInsightPicker from '../../OutcomeInsightPicker';

const styles = theme => ({
  root: {
    borderRadius: theme.radius.md,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  dataCellContainer: {
    display: 'flex',
    gap: '1rem', // theme.spacing(2) won't work
  },
  inlineHeader: {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: theme.spacing(1),
  },
  marginLeft: {
    marginLeft: 'auto',
  },
  strategiesContainer: {
    margin: theme.spacing(1, 0),
  },
  strategiesTitle: {
    marginBottom: theme.spacing(0.5),
  },
  strategiesChipContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  options: {
    display: 'flex',
    flexDirection: 'row',
  },
  accordion: {
    boxShadow: 'initial',
  },
  summary: {
    alignItems: 'flex-start',
  },
  details: {
    borderTop: `1px ${theme.palette.facet.accentGray} solid`,
    flexDirection: 'column',
  },
  detailFields: {
    display: 'flex',
    flexDirection: 'row',
    gap: '1rem',
    paddingBottom: theme.spacing(1),
  },
});

const OutcomeAccordionSummary = withStyles((theme) => ({
  root: { paddingBottom: theme.spacing(2) },
  content: {
    display: 'flex',
    flexDirection: 'column',
    margin: '0px',
    '&$expanded': {
      margin: '0px 0',
    },
    minWidth: 0,
  },
  expanded: {},
  expandIcon: {
    display: 'flex',
    flexDirection: 'column',
  },
}))(AccordionSummary);

const SummaryCard = ({ classes }) => {
  const { logStandardizedEvent } = useEventTrackingLogEvent();
  const { resource } = useMetaLayoutContext();

  const {
    refreshPageContentCollection,
  } = usePageContentContext();

  const { loadModalCollectionByLink } = useModalContext();

  async function handleDelete() {
    if (resource.id() > 0 && resource.meta().action('DELETE')) {
      return resource.delete();
    }
    return null;
  }

  const {
    open,
    openPopover,
    handleClose: handlePopoverClose,
    handleEnter,
  } = usePopoverState({});

  const optionsRef = React.useRef();

  const {
    dialogOpen,
    loading,
    handleSubmit,
    handleOpen,
    handleClose,
  } = useConfirmationDialogState({
    onSubmit: handleDelete,
  });

  async function handleConfirm() {
    await handleSubmit();
    // intentionally not awaiting refreshPageContentCollection to close modal
    refreshPageContentCollection();
  }

  const canEdit = !!resource.relationship(fr.relationships.facetPlanOutcomeWizardNavigation.name);
  const canDelete = !!resource.meta().action('DELETE');

  const mainFields = [];
  const additionalFields = [];
  switch (resource.attr('outcomeType')) {
    case 'emergencyFund':
      mainFields.push('emergencyFundMonthsCovered');
      mainFields.push('emergencyFundExpensesToCover');
      mainFields.push('targetAmount');
      mainFields.push('emergencyFundReachedDate');
      mainFields.push('amountAllocatedOverNextNMonths');
      break;
    case 'generalSavings':
      mainFields.push('generalSavingsActionOnceFundedDisplay');
      mainFields.push('projectedAmountOnEndDate');
      mainFields.push('endDate');
      mainFields.push('amountAllocatedOverNextNMonths');
      additionalFields.push('targetAmount');
      additionalFields.push('targetEndDate');
      break;
    case 'payOffDebt':
      mainFields.push('debtTotalBalanceNow');
      mainFields.push('debtWeightedAverageRateNow');
      mainFields.push('endDate');
      mainFields.push('debtInterestPaid');
      mainFields.push('amountContributedOverNextNMonths');
      additionalFields.push('targetEndDate');
      break;
    case 'education':
      mainFields.push('educationTypeDisplay');
      mainFields.push('percentOfEducationFund');
      mainFields.push('startDate');
      mainFields.push('educationStartDate');
      mainFields.push('amountAllocatedOverNextNMonths');
      additionalFields.push('ageOfEducationStart');
      additionalFields.push('totalSavedAtEducationStart');
      additionalFields.push('educationYearsInSchool');
      break;
    case 'retirement':
      mainFields.push('retirementAge');
      mainFields.push('retirementEndOfPlanAge');
      mainFields.push('startDate');
      mainFields.push('retirementTotalAnnualAllocationsPreTax');
      break;
    default:
      break;
  }
  const [expanded, setExpanded] = useState(false);

  return (
    <Card className={classNames(classes.root)}>
      <Accordion
        className={classes.accordion}
        ref={optionsRef}
        expanded={expanded}
        onChange={(_, newExpandedValue) => setExpanded(newExpandedValue)}
      >
        <OutcomeAccordionSummary
          expandIcon={<ExpandMoreIcon test={'accordion-summary-card-expand-icon'} />}
          className={classes.summary}
        >
          <div className={classes.inlineHeader}>
            <Box flexBasis={0} flexGrow={1} minWidth={0}>
              <Typography
                weight={'bold'}
                color={'black'}
                variant={'h4'}
                noWrap
              >
                {resource.attr('title')}
              </Typography>
            </Box>
            <FDStack direction="row">
              <OutcomeInsightPicker
                resource={resource}
              />
              <QuickEditPicker
                resource={resource}
              />
              {(canEdit || canDelete)
                ? (
                  <React.Fragment>
                    <IconButton
                      className={classes.marginLeft}
                      onClick={(event) => {
                        event.stopPropagation();
                        return openPopover(event);
                      }}
                      test={`facet-plan-summary-card-options-${resource.attr('title')}`}
                    >
                      <MoreVertIcon />
                    </IconButton>
                    <Popover
                      open={open}
                      anchorEl={optionsRef.current}
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                      onClose={handlePopoverClose}
                      onEnter={handleEnter}
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: 'center',
                        horizontal: 'right',
                      }}
                    >
                      <List>
                        {resource.relationship(fr.relationships.facetPlanOutcomeWizardNavigation.name) ? (
                          <ListItem
                            button
                            onClick={() => {
                              loadModalCollectionByLink(resource.relationship(fr.relationships.facetPlanOutcomeWizardNavigation.name).link('related').url());
                              handlePopoverClose();
                              logStandardizedEvent(categories.whatIf, eventTypes.clickButton, 'advanced editing outcome');
                            }}
                            disabled={!canEdit}
                            test={'facet-plan-summary-card-option-edit'}
                          >
                            <Typography>{'Advanced Editing'}</Typography>
                          </ListItem>
                        ) : null}
                        {canDelete ? (
                          <ListItem
                            button
                            onClick={async () => {
                              handleOpen();
                            }}
                            disabled={!canDelete}
                            test={'facet-plan-summary-card-option-delete'}
                          >
                            <Typography color="red">{'Delete'}</Typography>
                          </ListItem>
                        ) : null}
                        {dialogOpen && (
                          <NewDialog
                            title={'Are you sure?'}
                            message={'Removing this item will delete all of its information. This cannot be undone.'}
                            cancelLabel={'Nevermind'}
                            confirmLabel={'Remove'}
                            confirmButtonType="error"
                            loading={loading}
                            onConfirm={async () => {
                              await handleConfirm();
                              handlePopoverClose();
                            }}
                            onCancel={() => {
                              handleClose();
                              handlePopoverClose();
                            }}
                            onClose={() => {
                              handleClose();
                              handlePopoverClose();
                            }}
                            showCloseIcon={false}
                          />
                        )}
                      </List>
                    </Popover>
                  </React.Fragment>
                ) : null}
            </FDStack>
          </div>
          <div className={classNames(classes.dataCellContainer)}>
            {mainFields.map((fieldName) => (
              <MetaControlField
                key={`${resource.id()}${fieldName}`}
                attr={fieldName}
                hideViaMetaOverride
              />
            ))}
          </div>
        </OutcomeAccordionSummary>
        <AccordionDetails className={classes.details}>
          {/* -------------- ADDITIONAL FIELDS -------------- */}
          <div className={classes.detailFields}>
            {additionalFields.map((fieldName) => (
              <MetaControlField
                key={`${resource.id()}${fieldName}`}
                attr={fieldName}
                hideViaMetaOverride
              />
            ))}
          </div>

          {/* -------------- STRATEGY CHIPS -------------- */}

          <div className={classNames(classes.strategiesContainer)}>
            <Typography
              className={classNames(classes.strategiesTitle)}
              variant={'body1'}
              weight={'medium'}
              color={'black'}
            >
              {'Strategies'}
            </Typography>
            <div className={classNames(classes.strategiesChipContainer)}>
              {resource.attr('strategySummaries')?.resources().list().map(strategyItem => (
                <ModifyStrategyButtonPicker key={strategyItem.id()} resource={strategyItem} />
              ))}
              <AddStrategyButtonPicker resource={resource} />
            </div>
          </div>
          {expanded ? (
            <AccountTable
              resource={resource}
              classes={{
                root: classes.strategiesContainer,
                title: classes.strategiesTitle,
              }}
            />
          ) : null}
        </AccordionDetails>
      </Accordion>
    </Card>
  );
};

SummaryCard.defaultProps = {};

SummaryCard.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(SummaryCard);
